import { useEffect, useState, useCallback } from "react";
import { Coins, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Compact horizontal strip that shows the last-24h estimated LLM spend
 * per burstable daemon, plus a total. Sits under the Progress Dash
 * header. All numbers are estimates (docs stamped in last 24h × unit
 * cost), not billed spend.
 */
export default function TokenBudgetStrip() {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/progress/token-budget");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load token budget");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="mt-6 border border-stone-200 rounded-xl p-4 bg-white text-sm text-stone-500">
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading token budget…
      </div>
    );
  }
  if (err) {
    return (
      <div className="mt-6 border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const total = data.total_est_usd_24h ?? 0;
  const rows  = (data.rows || []);

  return (
    <div
      className="mt-6 border border-stone-200 rounded-xl p-4 bg-white shadow-sm"
      data-testid="token-budget-strip"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-amber-50">
            <Coins className="h-4 w-4 text-amber-700" />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-900">
              LLM spend · last 24h
              <span className="ml-2 text-[10px] tracking-widest text-stone-500 font-underground-sc">
                real usage
              </span>
            </div>
            <div className="text-[11px] text-stone-500">
              Sourced from llm_usage — real SDK token counts for direct provider calls; tiktoken estimate on Emergent-key path.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] tracking-widest text-stone-500 font-underground-sc">
              Total est.
            </div>
            <div
              className="text-2xl font-semibold text-stone-900"
              data-testid="token-budget-total"
            >
              ${total.toFixed(2)}
            </div>
          </div>
          <button
            onClick={load}
            className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
            data-testid="token-budget-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-3 text-[12px] text-stone-500">
          No LLM calls recorded in the last 24 hours.
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
          {rows.map((r) => (
            <div
              key={r.program}
              data-testid={`token-row-${r.program}`}
              className="rounded-lg border border-stone-100 p-2 flex items-baseline justify-between gap-2"
            >
              <div className="min-w-0">
                <div className="text-[11px] text-stone-700 font-medium truncate">
                  {r.program}
                </div>
                <div className="text-[10px] text-stone-500">
                  {r.calls?.toLocaleString?.() || 0} calls ·{" "}
                  {(r.total_tokens || 0).toLocaleString()} tok
                </div>
              </div>
              <div className="text-sm font-semibold text-stone-900 tabular-nums">
                ${(r.est_usd_24h || 0).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 text-[11px] text-stone-500">
        Source: {data.source || "llm_usage"} · {data.total_calls_24h || 0} calls ·{" "}
        {(data.total_tokens_24h || 0).toLocaleString()} tokens · window {data.window}
      </div>
    </div>
  );
}
